import { useCallback, useEffect } from 'react';
import { Logger } from '@saviynt/common';

import { type SortState } from '../../features/ViolationsTab/data-layer/violationsTabTypes';

import { useTypedSelector } from './use-typedselector';

const PERSONLIZATION_KEY = 'aag-personlization';

interface PersonalizationObjectState {
  sort?: SortState;
  tab?: string | null;
  filters: Record<string, string[][]>;
  columns: string[];
}

const getPersonlization = (key = PERSONLIZATION_KEY) => {
  const encodedString = localStorage.getItem(key);

  if (!encodedString) {
    return null;
  }

  try {
    const jsonString = decodeURIComponent(atob(encodedString));

    return JSON.parse(jsonString) as PersonalizationObjectState;
  } catch (e) {
    Logger.error('Failed to decode personalization data', e);

    return null;
  }
};

const usePersonalization = () => {
  const {
    global: { isGlobalLoading },
    violationsTab: {
      controls: { tabInAction, activeFilters, sortInAction, activeColumns },
    },
  } = useTypedSelector((state) => state.aag);

  const savePersonlization = useCallback(() => {
    const appliedFilters: Record<string, string[][]> = {};

    activeFilters.forEach((filter) => {
      appliedFilters[filter.value] = filter.options.map((option) => [
        option.value,
        option.label,
      ]);
    });

    const personlizationObject: PersonalizationObjectState = {
      sort: sortInAction,
      filters: appliedFilters,
      columns: activeColumns
        .filter((column) => column.isChecked)
        .map((column) => column.value),
    };

    if (tabInAction !== null) {
      personlizationObject.tab = tabInAction;
    }

    const jsonString = JSON.stringify(personlizationObject);
    const encodedString = btoa(encodeURIComponent(jsonString));

    localStorage.setItem(PERSONLIZATION_KEY, encodedString);
  }, [tabInAction, sortInAction, activeFilters, activeColumns]);

  useEffect(() => {
    if (!isGlobalLoading) {
      savePersonlization();
    }
  }, [isGlobalLoading, savePersonlization]);
};

export { getPersonlization, usePersonalization };
